import { Dispatch, useState } from 'react'
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
  useInfiniteQuery,
  useQuery,
} from 'react-query'
import {
  ErrorResponse,
  OrderList,
  Order,
  OrdersApi,
  OrderItemType,
  OrderStatus,
  PaymentMethod,
  RefundCreateParams,
} from '@citruscamps/citrus-client'
import {
  DefaultDetailsPagination,
  DefaultMainPagination,
  UnlimitedPagination,
} from '../../../../../constants/pagination'
import { useLocalStorage } from '../../../../../hooks/useLocalStorage'
import { useRouter } from '../../../../../hooks/useRouter'
import { useSearchValue } from '../../../../../hooks/useSearchValue'
import { Pagination } from '../../../../../interfaces/pagination'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateListKey } from '../../../../../utils/key-generator'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'
import { useUpdateOrders } from './useUpdateOrders'

export const DefaultOrderFetchProps: OrderFetchProps = {
  sort: 'updated',
  order: 'DESC',
  filter: {
    status: [],
    checkout_method: [],
    item_type: [],
    partially_paid: undefined,
  },
}

export interface OrderFilterProps {
  status?: OrderStatus[]
  checkout_method?: PaymentMethod[]
  contact_id?: string
  item_parent?: string[]
  item_type?: OrderItemType[]
  partially_paid?: boolean
  coupon?: string[]
}

export interface OrderFetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  filter: OrderFilterProps
}

interface IProps {
  programId?: string
  pagination?: Pagination
  fetchProps?: OrderFetchProps
  overwriteFilters?: boolean
}

interface FetchOrders {
  data: Order[]
  error: ErrorResponse | null
  fetchProps: OrderFetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  handleRefund: (order: Order, refund: RefundCreateParams) => Promise<void>
  handleCancel: (order: Order) => Promise<void>
  handleSendEmail: (order: Order) => any
  handleDispute: (order: Order) => any
  setFetchProps: (value?: OrderFetchProps) => void
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
}

/**
 * @deprecated TODO: Product financial foundation is not implemented yet
 * ⚠️ This will be removed in the future when we migrate to products
 **/
export const useFetchOrders = ({
  programId,
  pagination: initialPagination = DefaultMainPagination,
  overwriteFilters,
  ...props
}: IProps): FetchOrders => {
  const { requestHandler } = useRequestHandler()
  const { pathname } = useRouter()
  const [pagination, setPagination] = useState<Pagination>(initialPagination)
  const initialFetchProps: OrderFetchProps = props.fetchProps || DefaultOrderFetchProps
  const [fetchProps, setFetchProps] = useLocalStorage<OrderFetchProps>(
    `order-filter-${pathname}`,
    initialFetchProps,
    overwriteFilters,
  )
  const { search, deferSearch, setSearch, isSearching } = useSearchValue('', {
    onDefer: () => setPagination(initialPagination),
  })
  const queryKeys = generateListKey({
    type: 'order',
    program_id: programId,
    pagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.filter,
    search: deferSearch,
  })
  const {
    isLoading,
    isError,
    data: resp,
    error,
  } = useQuery<OrderList, ErrorResponse>(
    queryKeys,
    async ({ signal }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new OrdersApi(generateApiConfig())
      const skip = pagination.limit ? pagination.page * pagination.limit : undefined
      const response: OrderList = await requestHandler<OrderList>(() =>
        client.findOrders(
          {
            program_id: programId,
            limit: pagination.limit,
            skip,
            order: fetchProps.order || undefined,
            sort: fetchProps.sort || undefined,
            search: deferSearch || undefined,
            status:
              fetchProps?.filter?.status && fetchProps?.filter?.status.length > 0
                ? fetchProps?.filter.status
                : undefined,
            checkout_method:
              fetchProps?.filter?.checkout_method && fetchProps?.filter?.checkout_method.length > 0
                ? fetchProps?.filter.checkout_method
                : undefined,
            contact_id: fetchProps?.filter?.contact_id || undefined,
            item_type:
              fetchProps?.filter?.item_type && fetchProps?.filter?.item_type.length > 0
                ? fetchProps?.filter.item_type
                : undefined,
            partially_paid: fetchProps?.filter?.partially_paid || undefined,
            item_parent:
              fetchProps?.filter?.item_parent && fetchProps?.filter?.item_parent.length > 0
                ? fetchProps?.filter.item_parent
                : undefined,
            coupon:
              fetchProps?.filter?.coupon && fetchProps?.filter?.coupon.length > 0
                ? fetchProps?.filter.coupon
                : undefined,
          },
          { signal },
        ),
      )
      return response
    },
    { enabled: !!programId },
  )
  const { handleRefund, handleSendEmail, handleDispute, handleCancel } = useUpdateOrders({
    programId,
  })

  const _setFetchProps = (val?: OrderFetchProps) => {
    setPagination(initialPagination)
    return setFetchProps(val || DefaultOrderFetchProps)
  }

  return {
    data: resp?.data || [],
    error,
    fetchProps,
    isError,
    isLoading: isLoading || isSearching,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
    search,
    handleRefund,
    handleSendEmail,
    handleDispute,
    handleCancel,
    setFetchProps: _setFetchProps,
    setPagination,
    setSearch,
  }
}

interface FetchInfiniteOrder {
  data: Order[]
  error: ErrorResponse | null
  fetchProps: OrderFetchProps
  isError: boolean
  isFetching: boolean
  isFetchingNextPage: boolean
  isLoading: boolean
  hasNextPage: boolean
  pagination: Pagination
  search: string
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<OrderList, ErrorResponse>>
  handleRefund: (order: Order, refund: RefundCreateParams) => Promise<void>
  handleCancel: (order: Order) => Promise<void>
  handleSendEmail: (order: Order) => any
  handleDispute: (order: Order) => any
  setFetchProps: (value?: OrderFetchProps) => void
  setSearch: Dispatch<string>
}

/**
 * @deprecated TODO: Product financial foundation is not implemented yet
 * ⚠️ This will be removed in the future when we migrate to products
 **/
export const useFetchInfiniteOrders = ({
  programId = '',
  pagination = DefaultDetailsPagination,
  overwriteFilters,
  ...props
}: IProps): FetchInfiniteOrder => {
  const { requestHandler } = useRequestHandler()
  const { pathname } = useRouter()
  const initialFetchProps: OrderFetchProps = props.fetchProps || DefaultOrderFetchProps
  const [fetchProps, setFetchProps] = useLocalStorage<OrderFetchProps>(
    `order-filter-${pathname}`,
    initialFetchProps,
    overwriteFilters,
  )
  const { search, deferSearch, setSearch, isSearching } = useSearchValue('')
  const queryKeys = generateListKey({
    type: 'order',
    program_id: programId,
    pagination: UnlimitedPagination,
    sort: fetchProps.sort,
    order: fetchProps.order,
    query: fetchProps.filter,
    search: deferSearch,
  })
  const {
    data,
    error,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
    hasNextPage = false,
  } = useInfiniteQuery<OrderList, ErrorResponse>(
    queryKeys,
    async ({ signal, pageParam: pg = pagination }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new OrdersApi(generateApiConfig())
      const skip = pg.limit ? pg.page * pg.limit : undefined
      const resp: OrderList = await requestHandler<OrderList>(() =>
        client.findOrders(
          {
            program_id: programId,
            limit: pagination.limit,
            skip,
            order: fetchProps.order || undefined,
            sort: fetchProps.sort || undefined,
            search: deferSearch || undefined,
            status:
              fetchProps?.filter?.status && fetchProps?.filter?.status.length > 0
                ? fetchProps?.filter.status
                : undefined,
            checkout_method:
              fetchProps?.filter?.checkout_method && fetchProps?.filter?.checkout_method.length > 0
                ? fetchProps?.filter.checkout_method
                : undefined,
            contact_id: fetchProps?.filter?.contact_id || undefined,
            item_type:
              fetchProps?.filter?.item_type && fetchProps?.filter?.item_type.length > 0
                ? fetchProps?.filter.item_type
                : undefined,
            partially_paid: fetchProps?.filter?.partially_paid || undefined,
            item_parent:
              fetchProps?.filter?.item_parent && fetchProps?.filter?.item_parent.length > 0
                ? fetchProps?.filter.item_parent
                : undefined,
            coupon:
              fetchProps?.filter?.coupon && fetchProps?.filter?.coupon.length > 0
                ? fetchProps?.filter.coupon
                : undefined,
          },
          { signal },
        ),
      )
      return resp
    },
    {
      getNextPageParam: (resp: OrderList): Pagination | undefined => {
        if (!resp.page_count || resp.page < resp.page_count - 1) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page + 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      getPreviousPageParam: (resp: OrderList): Pagination | undefined => {
        if (!resp.page_count || resp.page <= resp.page_count) {
          return {
            ...pagination,
            count: resp?.count,
            total: resp?.total,
            page: resp?.page - 1,
            page_count: resp?.page_count,
          }
        }
        return undefined
      },
      enabled: !!programId,
    },
  )
  const { handleRefund, handleSendEmail, handleDispute, handleCancel } = useUpdateOrders({
    programId,
  })

  const _setFetchProps = (val?: OrderFetchProps) => {
    return setFetchProps(val || DefaultOrderFetchProps)
  }

  const resp = [...(data?.pages || [])].pop()
  return {
    data: (data?.pages || []).reduce<Order[]>((list, p) => [...list, ...p.data], []) || [],
    error,
    fetchProps,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading: isLoading || isSearching,
    pagination: {
      ...pagination,
      count: resp?.count || pagination.count,
      total: resp?.total || pagination.total,
      page: resp?.page || pagination.page,
      page_count: resp?.page_count || pagination.page_count,
    },
    search,
    fetchNextPage,
    handleRefund,
    handleSendEmail,
    handleDispute,
    handleCancel,
    setFetchProps: _setFetchProps,
    setSearch,
  }
}
